import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useMaxMd } from '@/hooks/useMaxMd'
import { getCapTable } from '@/services/waterfall'
import type { CapTableRow, CapTableResponse } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import WaterfallTabHeader from '../distribution-waterfall/WaterfallTabHeader'

const unitsFmt = new Intl.NumberFormat('en-US')

const pctFmt = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
})

const fmtPct = (value: number) => pctFmt.format(value / 100)

const CELL_OVERFLOW = 'overflow-hidden text-ellipsis'

const LABEL_COL_CLASS = `sticky left-0 z-20 w-36 min-w-[9rem] max-w-[9rem] border-r border-gray-200 ${CELL_OVERFLOW}`
const LABEL_COL_MOBILE_CLASS = `sticky left-0 z-20 w-28 min-w-[7rem] max-w-[7rem] border-r border-gray-200 ${CELL_OVERFLOW}`
const NUM_COL_CLASS = `w-[6.5rem] min-w-[6.5rem] max-w-[6.5rem] ${CELL_OVERFLOW}`
const NUM_COL_MOBILE_CLASS = `w-[6rem] min-w-[6rem] max-w-[6rem] ${CELL_OVERFLOW}`

const LABEL_COL_WIDTH = '9rem'
const LABEL_COL_MOBILE_WIDTH = '7rem'
const NUM_COL_WIDTH = '6.5rem'
const NUM_COL_MOBILE_WIDTH = '6rem'

const labelHead = (pad: string, col: string) =>
  `${col} ${pad} z-30 bg-gray-50 text-left font-medium text-gray-600 whitespace-nowrap`
const labelCell = (pad: string, col: string) =>
  `${col} ${pad} z-20 bg-white text-left whitespace-nowrap group-hover:bg-gray-50`
const childLabelCell = (pad: string, col: string) =>
  `${col} ${pad} z-20 bg-white text-left whitespace-nowrap`
const numHead = (pad: string, col: string) =>
  `${col} ${pad} text-right font-medium text-gray-600 whitespace-nowrap ${CELL_OVERFLOW}`
const numCell = (pad: string, col: string) =>
  `${col} ${pad} text-right tabular-nums whitespace-nowrap ${CELL_OVERFLOW}`
const groupHead = (pad: string) =>
  `${pad} text-center font-medium text-gray-600 whitespace-nowrap border-b border-gray-200`
const VESTED_GROUP_BORDER = 'border-l border-gray-200'
const TOTALS_ROW_BG = 'bg-blue-50'
const TOTALS_ROW_BORDER = 'border-t-2 border-t-blue-200'
const totalsLabelCell = (pad: string, col: string) =>
  `${col} ${pad} z-20 ${TOTALS_ROW_BG} text-left whitespace-nowrap ${CELL_OVERFLOW} ${TOTALS_ROW_BORDER} font-bold text-gray-900`

function UnitLedgerColgroup({ maxMd }: { maxMd: boolean }) {
  const labelWidth = maxMd ? LABEL_COL_MOBILE_WIDTH : LABEL_COL_WIDTH
  const numWidth = maxMd ? NUM_COL_MOBILE_WIDTH : NUM_COL_WIDTH
  return (
    <colgroup>
      <col style={{ width: labelWidth }} />
      <col style={{ width: numWidth }} />
      <col style={{ width: numWidth }} />
      <col style={{ width: numWidth }} />
      <col style={{ width: numWidth }} />
    </colgroup>
  )
}

function unitLedgerTableMinWidth(maxMd: boolean): string {
  const labelWidth = maxMd ? LABEL_COL_MOBILE_WIDTH : LABEL_COL_WIDTH
  const numWidth = maxMd ? NUM_COL_MOBILE_WIDTH : NUM_COL_WIDTH
  return `calc(${labelWidth} + 4 * ${numWidth})`
}
const totalsNumCell = (pad: string, col: string) =>
  `${numCell(pad, col)} ${TOTALS_ROW_BG} ${TOTALS_ROW_BORDER} font-bold text-gray-900`

type GroupBy = 'class' | 'unitholder'

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'unitholder', label: 'Unitholder' },
  { value: 'class', label: 'Class' },
]

function todayIso(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function sumRows(rows: CapTableRow[], field: 'granted_units' | 'vested_units'): number {
  return rows.reduce((sum, row) => sum + row[field], 0)
}

function pctOfGrand(part: number, grand: number): number {
  return grand > 0 ? (part / grand) * 100 : 0
}

interface RowGroup {
  key: string
  label: string
  rows: CapTableRow[]
  granted: number
  vested: number
}

function buildGroups(rows: CapTableRow[], groupBy: GroupBy): RowGroup[] {
  if (groupBy === 'class') {
    const byClass = new Map<string, CapTableRow[]>()
    for (const row of rows) {
      const list = byClass.get(row.class) ?? []
      list.push(row)
      byClass.set(row.class, list)
    }
    return ['A', 'B', 'C']
      .filter((cls) => byClass.has(cls))
      .map((cls) => {
        const classRows = byClass.get(cls) ?? []
        return {
          key: cls,
          label: `Class ${cls}`,
          rows: classRows,
          granted: sumRows(classRows, 'granted_units'),
          vested: sumRows(classRows, 'vested_units'),
        }
      })
  }

  const byMember = new Map<string, CapTableRow[]>()
  for (const row of rows) {
    const list = byMember.get(row.member) ?? []
    list.push(row)
    byMember.set(row.member, list)
  }

  return Array.from(byMember.entries()).map(([member, memberRows]) => ({
    key: member,
    label: memberRows[0]?.unitholder ?? member,
    rows: memberRows,
    granted: sumRows(memberRows, 'granted_units'),
    vested: sumRows(memberRows, 'vested_units'),
  }))
}

function childLabel(row: CapTableRow, groupBy: GroupBy): string {
  if (groupBy === 'class') return row.unitholder
  return `Class ${row.class}`
}

export default function UnitLedger() {
  const maxMd = useMaxMd()
  const cellPadClass = maxMd ? 'px-2 py-1.5' : 'px-3 py-2'
  const labelColClass = maxMd ? LABEL_COL_MOBILE_CLASS : LABEL_COL_CLASS
  const numColClass = maxMd ? NUM_COL_MOBILE_CLASS : NUM_COL_CLASS
  const labelHeadClass = labelHead(cellPadClass, labelColClass)
  const labelCellClass = labelCell(cellPadClass, labelColClass)
  const childLabelCellClass = childLabelCell(cellPadClass, labelColClass)
  const numHeadClass = numHead(cellPadClass, numColClass)
  const numCellClass = numCell(cellPadClass, numColClass)
  const groupHeadClass = groupHead(cellPadClass)
  const vestedUnitsHeadClass = `${numHeadClass} ${VESTED_GROUP_BORDER}`
  const vestedUnitsCellClass = `${numCellClass} ${VESTED_GROUP_BORDER}`
  const vestedGroupHeadClass = `${groupHeadClass} ${VESTED_GROUP_BORDER}`
  const totalsLabelCellClass = totalsLabelCell(cellPadClass, labelColClass)
  const totalsNumCellClass = totalsNumCell(cellPadClass, numColClass)
  const totalsVestedUnitsCellClass = `${totalsNumCellClass} ${VESTED_GROUP_BORDER}`

  const [asof, setAsof] = useState(todayIso)
  const [groupBy, setGroupBy] = useState<GroupBy>('unitholder')
  const [data, setData] = useState<CapTableResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const load = useCallback(async (date: string) => {
    setLoading(true)
    setError(null)
    try {
      setData(await getCapTable(date))
    } catch (e) {
      setError(formatApiError(e, 'Failed to load unit ledger'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(asof)
  }, [asof, load])

  const groups = useMemo(
    () => buildGroups(data?.rows ?? [], groupBy),
    [data?.rows, groupBy],
  )

  const showFutureNote = asof > todayIso()

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const groupKeys = useMemo(() => groups.map((g) => g.key), [groups])

  const allExpanded =
    groupKeys.length > 0 && groupKeys.every((key) => expanded.has(key))

  const toggleAll = () => {
    if (allExpanded) {
      setExpanded(new Set())
      return
    }
    setExpanded(new Set(groupKeys))
  }

  useEffect(() => {
    setExpanded(new Set(groupKeys))
  }, [groupBy, groupKeys])

  const grandGranted = sumRows(data?.rows ?? [], 'granted_units')
  const grandVested = sumRows(data?.rows ?? [], 'vested_units')

  const labelColumnHeader = groupBy === 'class' ? 'Class' : 'Unitholder'

  return (
    <div className="w-full space-y-6">
      <WaterfallTabHeader
        title="Unit ledger"
        description="Granted and vested units by class and unitholder as of a selected date."
      />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1">
            <label htmlFor="unit-ledger-asof" className="text-xs font-medium text-gray-600">
              As of
            </label>
            <input
              id="unit-ledger-asof"
              type="date"
              className="input w-full py-1.5 text-sm sm:w-[11rem]"
              value={asof}
              onChange={(e) => setAsof(e.target.value)}
            />
            {showFutureNote && (
              <p className="text-[11px] text-gray-400">
                Vested units reflect the schedule as of this future date.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1 sm:items-end">
            <span className="text-xs font-medium text-gray-600">Group by</span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={toggleAll}
                className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </button>
              <div
                className="inline-flex gap-1 bg-gray-200 p-1 rounded-lg min-w-max"
                role="group"
                aria-label="Group by"
              >
                {GROUP_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                      groupBy === option.value
                        ? 'bg-white text-brand-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => setGroupBy(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <p className="px-4 py-6 text-sm text-gray-500">Loading unit ledger…</p>
        )}
        {error && (
          <p className="px-4 py-6 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {!loading && !error && data && (
          <div className="table-scroll-mobile overflow-x-auto overscroll-x-contain px-4 pb-4">
            <table
              className="w-full table-fixed text-sm border-separate border-spacing-0"
              style={{ minWidth: unitLedgerTableMinWidth(maxMd) }}
            >
              <UnitLedgerColgroup maxMd={maxMd} />
              <thead className="bg-gray-50">
                <tr>
                  <th className={labelHeadClass} />
                  <th colSpan={2} className={groupHeadClass}>
                    Granted
                  </th>
                  <th colSpan={2} className={vestedGroupHeadClass}>
                    Vested
                    {showFutureNote ? (
                      <span className="ml-1 font-normal text-gray-400">(projected)</span>
                    ) : null}
                  </th>
                </tr>
                <tr>
                  <th className={labelHeadClass}>{labelColumnHeader}</th>
                  <th className={numHeadClass}>Units</th>
                  <th className={numHeadClass}>{maxMd ? '% Total' : 'Percent Total'}</th>
                  <th className={vestedUnitsHeadClass}>Units</th>
                  <th className={numHeadClass}>{maxMd ? '% Total' : 'Percent Total'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groups.map((group) => {
                  const isExpanded = expanded.has(group.key)
                  const canExpand = group.rows.length > 0

                  return (
                    <Fragment key={group.key}>
                      {isExpanded && canExpand
                        ? group.rows.map((row) => (
                            <tr
                              key={`${group.key}-${row.member}-${row.class}`}
                              className="text-xs text-gray-600 border-t border-gray-100"
                            >
                              <td
                                className={childLabelCellClass}
                                style={{ paddingLeft: maxMd ? '1.75rem' : '2.5rem' }}
                              >
                                {childLabel(row, groupBy)}
                              </td>
                              <td className={numCellClass}>
                                {unitsFmt.format(row.granted_units)}
                              </td>
                              <td className={numCellClass}>
                                {fmtPct(row.granted_pct_of_class)}
                              </td>
                              <td className={vestedUnitsCellClass}>
                                {unitsFmt.format(row.vested_units)}
                              </td>
                              <td className={numCellClass}>
                                {fmtPct(row.vested_pct_of_class)}
                              </td>
                            </tr>
                          ))
                        : null}
                      <tr
                        className={`group ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                      >
                        <td
                          className={`${labelCellClass} text-gray-700 font-medium ${
                            isExpanded ? 'bg-gray-50' : ''
                          }`}
                        >
                          {canExpand ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(group.key)}
                              className="inline-flex items-center gap-1.5 text-left hover:text-brand-700"
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <span className="text-gray-400 w-3">
                                {isExpanded ? '▾' : '▸'}
                              </span>
                              {group.label}
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 pl-[1.125rem]">
                              {group.label}
                            </span>
                          )}
                        </td>
                        <td
                          className={`${numCellClass} text-gray-800 ${
                            isExpanded ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {unitsFmt.format(group.granted)}
                        </td>
                        <td
                          className={`${numCellClass} text-gray-800 ${
                            isExpanded ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {fmtPct(pctOfGrand(group.granted, grandGranted))}
                        </td>
                        <td
                          className={`${vestedUnitsCellClass} ${
                            isExpanded ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {unitsFmt.format(group.vested)}
                        </td>
                        <td
                          className={`${numCellClass} text-gray-800 ${
                            isExpanded ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {fmtPct(pctOfGrand(group.vested, grandVested))}
                        </td>
                      </tr>
                    </Fragment>
                  )
                })}

                <tr aria-label="Unit ledger total">
                  <td className={totalsLabelCellClass}>Total</td>
                  <td className={totalsNumCellClass}>{unitsFmt.format(grandGranted)}</td>
                  <td className={totalsNumCellClass}>
                    {grandGranted > 0 ? fmtPct(100) : '—'}
                  </td>
                  <td className={totalsVestedUnitsCellClass}>{unitsFmt.format(grandVested)}</td>
                  <td className={totalsNumCellClass}>
                    {grandVested > 0 ? fmtPct(100) : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
